// v0.apex-edge-declined (02-2026) — RUM HTML TTFB collector.
//
// Phase 0 housekeeping sau khi đóng investigation apex edge Worker. Backend
// endpoint: POST /api/public/rum/nav-timing. Xem ADR
// /app/memory/adr-apex-edge-declined.md → mục "Reopen criteria" — nếu P75
// TTFB apex vượt threshold liên tục ≥7 ngày, quyết định "accept Emergent
// platform constraint" được mở lại.
//
// Nguyên tắc thiết kế:
// - CHỈ chạy sau `window.load` → không cản LCP.
// - 10% client sample → giữ tải backend thấp; peak trận đấu backend có
//   traffic_control tắt cả visit lẫn RUM (cùng bucket).
// - `navigator.sendBeacon` → non-blocking, request tồn tại kể cả khi user
//   điều hướng đi ngay lập tức.
// - Skip nếu Performance API không sẵn (bot, browser cũ).
// - Skip nếu embed mode (`?embed=1`) — reduced-runtime.
// - Best-effort, silent-fail: RUM lỗi TUYỆT ĐỐI KHÔNG được vỡ app.

const SAMPLE_RATE = 0.10;
const RUM_ENDPOINT = "/api/public/rum/nav-timing";

// visitor_id sync với visitors.py (cookie/localStorage `pl_vid`).
function _getVisitorId() {
  try {
    const key = "pl_vid";
    let v = window.localStorage.getItem(key);
    if (v && v.length >= 8) return v;
    // Fallback: sinh UUID-lite (16 hex chars) — không critical strong random.
    v = Array.from(window.crypto.getRandomValues(new Uint8Array(12)))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
    window.localStorage.setItem(key, v);
    return v;
  } catch {
    // localStorage disabled → session-only id là đủ.
    return "anon-" + Math.random().toString(16).slice(2, 14);
  }
}

function _isEmbed() {
  try {
    return new URLSearchParams(window.location.search).get("embed") === "1";
  } catch {
    return false;
  }
}

function _clampInt(v, max) {
  if (typeof v !== "number" || !Number.isFinite(v) || v < 0) return null;
  const i = Math.round(v);
  return i > max ? null : i;
}

// Đọc PerformanceNavigationTiming (Level 2). Fallback Level 1 khi thiếu.
function _extractNavTiming() {
  try {
    const nav = performance.getEntriesByType("navigation")[0];
    if (nav) {
      return {
        ttfb_ms: _clampInt(nav.responseStart - nav.requestStart, 60_000),
        dcl_ms: _clampInt(nav.domContentLoadedEventEnd - nav.startTime, 600_000),
        load_ms: _clampInt(nav.loadEventEnd - nav.startTime, 600_000),
        connect_ms: _clampInt(nav.connectEnd - nav.connectStart, 60_000),
      };
    }
  } catch {
    // fall through
  }
  // Level 1 fallback (deprecated nhưng vẫn khả dụng trên Safari cũ).
  try {
    const t = performance.timing;
    if (t && t.responseStart && t.requestStart) {
      return {
        ttfb_ms: _clampInt(t.responseStart - t.requestStart, 60_000),
        dcl_ms: _clampInt(t.domContentLoadedEventEnd - t.navigationStart, 600_000),
        load_ms: _clampInt(t.loadEventEnd - t.navigationStart, 600_000),
        connect_ms: _clampInt(t.connectEnd - t.connectStart, 60_000),
      };
    }
  } catch {
    // fall through
  }
  return null;
}

function _reportOnce() {
  if (_isEmbed()) return;
  if (Math.random() >= SAMPLE_RATE) return;

  const nav = _extractNavTiming();
  if (!nav || nav.ttfb_ms == null) return;

  // Path chuẩn hoá: bỏ query (không leak sensitive) + cắt 300 chars server-side.
  const path = (window.location.pathname || "/").slice(0, 300);

  const ect = (() => {
    try {
      return (navigator.connection && navigator.connection.effectiveType) || null;
    } catch {
      return null;
    }
  })();

  const payload = {
    visitor_id: _getVisitorId(),
    path,
    ttfb_ms: nav.ttfb_ms,
    dcl_ms: nav.dcl_ms,
    load_ms: nav.load_ms,
    connect_ms: nav.connect_ms,
    ect,
  };

  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  const url = `${base}${RUM_ENDPOINT}`;
  try {
    const body = new Blob([JSON.stringify(payload)], { type: "application/json" });
    if (navigator.sendBeacon && navigator.sendBeacon(url, body)) return;
    // Fallback fetch keepalive.
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: "omit",
    }).catch(() => { /* silent */ });
  } catch {
    // silent
  }
}

// Gắn sau window.load để không ảnh hưởng LCP + timing đã ổn định (loadEventEnd
// đã > 0). Nếu document đã load rồi (script mount sau load) → fire tiếp.
export function installRumHtmlTtfb() {
  try {
    if (typeof window === "undefined" || typeof performance === "undefined") return;
    if (document.readyState === "complete") {
      // Timing tối thiểu đã sẵn; defer 1 tick để loadEventEnd hoàn tất.
      setTimeout(_reportOnce, 0);
    } else {
      window.addEventListener("load", () => setTimeout(_reportOnce, 0), { once: true });
    }
  } catch {
    // silent
  }
}
